import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Sparkles, Timer, Target } from "lucide-react";
import { api } from "../lib/api";
import { Skeleton } from "./ui/skeleton";
import Countdown from "./Countdown";

export default function SignatureCard({ propertyId }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/score`).then(({ data }) => setS(data)).catch(() => setS(null));
  }, [propertyId]);

  if (!s) return <Skeleton className="h-64 w-full" />;
  const scoreColor = s.total >= 80 ? "text-emerald-400" : s.total >= 60 ? "text-blue-400" : s.total >= 40 ? "text-amber-400" : "text-rose-400";
  const probColor = s.closing_probability >= 70 ? "text-emerald-400" : s.closing_probability >= 40 ? "text-amber-400" : "text-rose-400";

  return (
    <section className="relative overflow-hidden rounded-xl bg-[hsl(222,47%,11%)] text-white p-6 md:p-8" data-testid="signature-card">
      {/* Ambient background */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(59,130,246,0.35), transparent 45%), radial-gradient(circle at 85% 75%, rgba(59,130,246,0.20), transparent 45%)"
      }} />

      <div className="relative">
        {/* Brand mark */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1 h-6 bg-primary rounded-full" />
          <span className="text-[10px] uppercase tracking-[0.3em] text-slate-300 font-semibold">LOOK OFF MARKET™</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Left column: Opportunity Index */}
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1.5 mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Opportunity Index
            </div>
            <div className="flex items-baseline gap-1">
              <span className={`font-heading text-7xl md:text-8xl font-bold tabular-nums leading-none ${scoreColor}`} data-testid="signature-total">{s.total}</span>
              <span className="text-xl text-slate-400">/100</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              <div className="inline-flex items-center gap-1.5 bg-white/5 rounded-md px-2.5 py-1">
                <Target className={`w-3.5 h-3.5 ${probColor}`} />
                <span className="text-slate-400">Probabilità chiusura</span>
                <span className={`font-heading font-semibold tabular-nums ${probColor}`} data-testid="signature-probability">{s.closing_probability}%</span>
              </div>
              <div className="inline-flex items-center gap-1.5 bg-white/5 rounded-md px-2.5 py-1">
                <Timer className="w-3.5 h-3.5 text-slate-300" />
                <span className="text-slate-400">Tempo stimato</span>
                <span className="font-heading font-semibold tabular-nums text-white" data-testid="signature-days">{s.estimated_days} giorni</span>
              </div>
            </div>
            {s.countdown && s.countdown.days_remaining != null && (
              <div className="mt-4"><Countdown countdown={s.countdown} dark /></div>
            )}
          </div>

          {/* Right column: reasons */}
          <div className="lg:col-span-2">
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-4">Perché?</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5" data-testid="signature-reasons">
              {s.signature_reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  {r.ok
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    : <XCircle className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />}
                  <span className={r.ok ? "text-white" : "text-slate-500 line-through"}>{r.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-white/10 text-[11px] text-slate-500">
          Valutazione strutturata basata su margine, posizione, complessità legale, documentazione, tempo di operazione e compatibilità investitori.
        </div>
      </div>
    </section>
  );
}
